'use client';

import type { RowScope } from '@unifyapps/carbon/no-code/utils/repeatables/attachRowScopeIdentities';
import type React from 'react';
import { useMemo } from 'react';
import { createContext, useContextSelector } from 'use-context-selector';

export type BlockRuntimeStateContextType = {
  data?: Record<string, Record<string, unknown>>;
  runTimeBlockKey?: string;
  // per-level stack, inner-first ordering. levels[0] = closest ancestor Repeatable.
  levels?: readonly { parentBlockId: string; instanceKey: string }[];
};

export const BlockRuntimeStateContext = createContext<BlockRuntimeStateContextType | undefined>(
  undefined,
);

export const useBlockRuntimeData = () => {
  const data = useContextSelector(BlockRuntimeStateContext, (context) => context?.data);
  return { data };
};

export const useRuntimeBlockKey = () => {
  const runTimeBlockKey = useContextSelector(
    BlockRuntimeStateContext,
    (context) => context?.runTimeBlockKey,
  );
  return runTimeBlockKey;
};

export const useRuntimeBlockLevels = () => {
  const levels = useContextSelector(BlockRuntimeStateContext, (context) => context?.levels);
  return levels;
};

export const useRowScopeStack = () => {
  const levels = useRuntimeBlockLevels();
  const rowScopeStack = useMemo<readonly RowScope[] | undefined>(() => {
    if (!levels || levels.length === 0) return undefined;
    return levels;
  }, [levels]);
  return rowScopeStack;
};

export function BlockRuntimeStateContextProvider({
  data,
  runTimeBlockKey,
  levels,
  children,
}: {
  data?: Record<string, Record<string, unknown>>;
  runTimeBlockKey?: string;
  levels?: readonly { parentBlockId: string; instanceKey: string }[];
  children: React.ReactNode;
}) {
  const value = useMemo(
    () => ({
      data,
      runTimeBlockKey,
      levels,
    }),
    [data, runTimeBlockKey, levels],
  );

  return (
    <BlockRuntimeStateContext.Provider value={value}>{children}</BlockRuntimeStateContext.Provider>
  );
}

// this ResetBlockRuntimeStateProvider is used to reset the access of parent block runtime in child block
// the use case of this is that when we render a Block that renders InterfacePageResourceLoader(or renders a page in simple terms) nested inside Repeatable, we don't want the child blocks of the new page to consider themselves as repeatable runtime block
// passing `value={undefined}` also clears `levels` (part of BlockRuntimeStateContextType), so the RowScope stack resets at this boundary alongside `runTimeBlockKey`.
export function ResetBlockRuntimeStateProvider({ children }: { children: React.ReactNode }) {
  return (
    <BlockRuntimeStateContext.Provider value={undefined}>
      {children}
    </BlockRuntimeStateContext.Provider>
  );
}
